import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatBytes } from '@xing-com/crate-jobs-domain-apply-helpers';
import { IconCross, IconFile } from '@xing-com/icons';
import { RadioButton } from '@xing-com/radio-button';
import { AbsoluteTimestamp } from '@xing-com/timestamp';

import {
  DeleteButton,
  DownloadLink,
  FileNameBodyCopy,
  IconContainer,
  NoCvBodyCopy,
  RadioButtonContainer,
  StyledListItem,
  StyledMeta,
  StyledProgressBar,
  TextContentWrapper,
} from './file-list-item.styles';

type FilePreviewNwtEventProps = {
  eventSchema: 'basic';
  event: string;
  context: string;
  element: string;
  page: string;
};

type FileListItemProps = {
  itemId: string;
  trackNwtEvent: (eventProps: FilePreviewNwtEventProps) => void;
  name?: string;
  Icon?: React.FC;
  isRadioDisabled?: boolean;
  isSelected?: boolean;
  lastUsedAt?: string;
  onDelete?: () => void;
  onRadioButtonClick?: (cvFileItemKey: string) => void;
  progress?: number;
  size?: number;
  url?: string;
};

export const NONE_ITEM_ID = 'none';

export const FileListItem: React.FC<FileListItemProps> = ({
  itemId,
  trackNwtEvent,
  name,
  Icon = IconFile,
  isRadioDisabled,
  isSelected,
  lastUsedAt,
  onDelete,
  onRadioButtonClick,
  progress,
  size,
  url,
}) => {
  const { formatMessage } = useIntl();
  const isUploading = progress !== undefined && progress !== 100;
  const onRadioClick = useCallback((): void => {
    onRadioButtonClick?.(itemId);
  }, [itemId, onRadioButtonClick]);
  const isListItemWithCvSelected = itemId !== NONE_ITEM_ID;

  const onLinkClick = useCallback(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      event: 'downloaded',
      context: 'instant_apply',
      element: 'saved_cv',
      page: 'jobs/apply',
    });
  }, [trackNwtEvent]);

  return (
    // @ts-expect-error FIXME: SC6
    <StyledListItem variant="left" $isSelected={isSelected}>
      {!isUploading && onRadioButtonClick ? (
        <RadioButtonContainer>
          <RadioButton
            disabled={isRadioDisabled}
            on={isSelected}
            onChange={onRadioClick}
            name="upload-list-selected"
            aria-label={name}
          />
        </RadioButtonContainer>
      ) : null}
      {isUploading ? (
        <StyledProgressBar
          progress={progress}
          data-testid="uploadProgressBar"
        />
      ) : isListItemWithCvSelected ? (
        <DownloadLink
          href={url}
          download
          target="_blank"
          $isEmptyLink={!url}
          onClick={url ? onLinkClick : undefined}
        >
          <IconContainer>
            <Icon aria-hidden />
          </IconContainer>
          <TextContentWrapper>
            <FileNameBodyCopy fontWeight="bold" size="medium" noMargin>
              {name}
            </FileNameBodyCopy>
            <StyledMeta size="small">
              {lastUsedAt ? (
                <AbsoluteTimestamp
                  date={new Date(lastUsedAt)}
                  variant="longDateFull"
                />
              ) : (
                <span>
                  <FormattedMessage id="JOBS_INSTANT_APPLY_LAST_UPLOADED_CV_LABEL" />
                </span>
              )}
              {size ? <span>{formatBytes(size, 0)}</span> : null}
            </StyledMeta>
          </TextContentWrapper>
        </DownloadLink>
      ) : (
        <NoCvBodyCopy fontWeight="bold" size="medium" noMargin>
          {name}
        </NoCvBodyCopy>
      )}
      {onDelete ? (
        <DeleteButton
          icon={IconCross}
          onClick={onDelete}
          data-testid="deleteUploadButton"
          aria-label={formatMessage(
            {
              id: 'JOBS_INSTANT_APPLY_FORM_DELETE_FILE_UPLOAD_BUTTON_A11Y_WEB',
            },
            { fileName: name }
          )}
        />
      ) : null}
    </StyledListItem>
  );
};
